<script setup lang="ts">
import { ref } from 'vue'
import { ChevronDown } from 'lucide-vue-next'

export interface DropdownOption {
  key: string
  label: string
  meta?: string
  badge?: string
  badgeVariant?: 'connected' | 'disconnected'
  iconColor?: string
  metaData?: Record<string, unknown>
}

withDefaults(defineProps<{
  options: DropdownOption[]
  modelValue?: string
  triggerLabel?: string
  triggerMeta?: string
  triggerBadge?: string
  triggerBadgeVariant?: 'connected' | 'disconnected'
  triggerIconColor?: string
  label?: string
  emptyMessage?: string
}>(), {
  label: '',
  triggerLabel: 'Select',
  emptyMessage: 'No options available.',
  triggerBadgeVariant: 'disconnected',
})

const emit = defineEmits<{
  select: [key: string]
}>()

const open = ref(false)

function toggle() {
  open.value = !open.value
}

function close() {
  open.value = false
}

function select(key: string) {
  emit('select', key)
  close()
}
</script>

<template>
  <div class="dropdown" :class="{ 'is-open': open }">
    <div v-if="label" class="dropdown-label">{{ label }}</div>

    <button
      class="dropdown-trigger"
      type="button"
      :aria-expanded="open"
      @click="toggle"
    >
      <span
        v-if="triggerIconColor"
        class="trigger-dot"
        :style="{ backgroundColor: triggerIconColor }"
      />
      <span class="trigger-copy">
        <span class="trigger-label">{{ triggerLabel }}</span>
        <span v-if="triggerMeta" class="trigger-meta">{{ triggerMeta }}</span>
      </span>
      <span
        v-if="triggerBadge"
        class="trigger-badge"
        :class="`badge-${triggerBadgeVariant}`"
      >
        {{ triggerBadge }}
      </span>
      <ChevronDown class="trigger-arrow" :class="{ 'arrow-open': open }" />
    </button>

    <Transition name="dropdown">
      <div v-if="open" class="dropdown-panel">
        <slot name="header" :open="open" />

        <template v-if="options.length > 0">
          <button
            v-for="option in options"
            :key="option.key"
            class="dropdown-item"
            :class="{ 'item-active': option.key === modelValue }"
            type="button"
            role="option"
            :aria-selected="option.key === modelValue"
            @click="select(option.key)"
          >
            <slot name="item" :option="option" :active="option.key === modelValue">
              <span
                v-if="option.iconColor"
                class="item-dot"
                :style="{ backgroundColor: option.iconColor }"
              />
              <span class="item-copy">
                <span class="item-label">{{ option.label }}</span>
                <span v-if="option.meta" class="item-meta">{{ option.meta }}</span>
              </span>
              <span
                v-if="option.badge"
                class="item-badge"
                :class="`badge-${option.badgeVariant}`"
              >
                {{ option.badge }}
              </span>
            </slot>
          </button>
        </template>

        <div v-else class="dropdown-empty">
          <slot name="empty">
            <span class="empty-text">{{ emptyMessage }}</span>
          </slot>
        </div>

        <slot name="footer" :open="open" />
      </div>
    </Transition>
  </div>

  <div v-if="open" class="dropdown-backdrop" @click="close" />
</template>

<style scoped>
.dropdown {
  position: relative;
}

.dropdown-label {
  margin-bottom: 0.35rem;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--color-ink-soft);
}

.dropdown-trigger {
  width: 100%;
  border: 0;
  background: transparent;
  text-align: left;
  display: grid;
  grid-template-columns: auto 1fr auto auto;
  gap: 0.5rem;
  align-items: center;
  padding: 0;
  cursor: pointer;
  border-radius: var(--radius-sm);
  outline: none;
}

.dropdown-trigger:focus-visible .trigger-copy {
  outline: 2px solid var(--color-primary);
  outline-offset: 2px;
  border-radius: 2px;
}

.trigger-dot {
  width: 0.6rem;
  height: 0.6rem;
  border-radius: 999px;
  flex-shrink: 0;
  box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.9);
  transition: transform 0.18s ease;
}

.dropdown-trigger:hover .trigger-dot {
  transform: scale(1.15);
}

.trigger-copy {
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.trigger-label {
  font-size: 0.84rem;
  font-weight: 600;
  color: var(--color-ink);
  line-height: 1.2;
}

.trigger-meta {
  font-size: 0.7rem;
  color: var(--color-ink-soft);
  line-height: 1.2;
}

.trigger-badge,
.item-badge {
  display: inline-flex;
  align-items: center;
  border-radius: var(--radius-sm);
  padding: 0.18rem 0.45rem;
  font-size: 0.68rem;
  font-weight: 600;
  white-space: nowrap;
}

.badge-connected {
  background: rgba(16, 185, 129, 0.1);
  color: var(--color-success);
}

.badge-disconnected {
  background: rgba(239, 68, 68, 0.09);
  color: var(--color-error);
}

.trigger-arrow {
  color: var(--color-ink-soft);
  transition: transform 0.22s cubic-bezier(0.34, 1.56, 0.64, 1);
  flex-shrink: 0;
}

.arrow-open {
  transform: rotate(-180deg);
}

/* ── Panel ── */
.dropdown-panel {
  position: absolute;
  left: 0;
  right: 0;
  margin-top: 0.55rem;
  border-radius: var(--radius-sm);
  border: 1px solid var(--color-border-strong);
  background: white;
  overflow: hidden;
  box-shadow:
    0 4px 6px -1px rgba(21, 32, 51, 0.06),
    0 8px 20px rgba(21, 32, 51, 0.10);
  z-index: 50;
}

.dropdown-item {
  width: 100%;
  border: 0;
  background: transparent;
  text-align: left;
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 0.5rem;
  align-items: center;
  padding: 0.55rem 0.7rem;
  cursor: pointer;
  transition: background 0.12s ease;
  outline: none;
}

.dropdown-item + .dropdown-item {
  border-top: 1px solid var(--color-border-soft);
}

.dropdown-item:focus-visible {
  background: rgba(79, 70, 229, 0.05);
}

.dropdown-item.item-active {
  background: rgba(79, 70, 229, 0.04);
}

.dropdown-item:not(.item-active):hover {
  background: rgba(79, 70, 229, 0.05);
}

.item-dot {
  width: 0.55rem;
  height: 0.55rem;
  border-radius: 999px;
  flex-shrink: 0;
}

.item-copy {
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.item-label {
  font-size: 0.84rem;
  font-weight: 600;
  color: var(--color-ink);
}

.item-meta {
  font-size: 0.7rem;
  color: var(--color-ink-soft);
}

.dropdown-empty {
  padding: 0.7rem;
}

.empty-text {
  color: var(--color-ink-soft);
  font-size: 0.78rem;
}

/* ── Backdrop ── */
.dropdown-backdrop {
  position: fixed;
  inset: 0;
  z-index: 40;
}

/* ── Animation ── */
.dropdown-enter-active {
  transition:
    opacity 0.18s ease,
    transform 0.22s cubic-bezier(0.16, 1, 0.3, 1);
}

.dropdown-leave-active {
  transition:
    opacity 0.14s ease,
    transform 0.16s ease-in;
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-6px) scale(0.98);
  transform-origin: top center;
}
</style>
